import { useCallback, useContext, useEffect, useState } from 'react';
import { Context } from '../../AuthContext';

// Data layer for the Publications-page References panel. Mirrors the
// links/notes/tasks pattern: optimistic local updates backed by the
// vertical-scoped reference endpoints. Lives above <References> so the list
// survives the panel being collapsed/reopened.
export default function useReferences(verticalId) {
  const { user } = useContext(Context);
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);

  const authFetch = useCallback(
    async (path, options = {}) => {
      const token = await user.getIdToken();
      return fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}${path}`, {
        ...options,
        headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
      });
    },
    [user]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.uid || !verticalId) return;
      setLoading(true);
      try {
        const res = await authFetch(`get_references?vertical_id=${verticalId}`);
        const data = await res.json();
        if (!cancelled) setReferences(data.data || []);
      } catch (error) {
        console.error('Failed to fetch references:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, verticalId, authFetch]);

  // Wait for the server-assigned _id so subsequent edit/delete/reorder work.
  const addReference = async (fields) => {
    try {
      const res = await authFetch('add_reference', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fields, vertical_id: verticalId }),
      });
      const result = await res.json();
      if (!result?._id) return;
      setReferences((prev) => [...prev, { ...fields, _id: result._id, vertical_id: verticalId }]);
    } catch (error) {
      console.error('Failed to add reference:', error);
    }
  };

  const editReference = async (id, fields) => {
    setReferences((prev) => prev.map((r) => (r._id === id ? { ...r, ...fields } : r)));
    try {
      await authFetch(`edit_reference?id=${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fields, vertical_id: verticalId }),
      });
    } catch (error) {
      console.error('Failed to edit reference:', error);
    }
  };

  const deleteReference = async (id) => {
    setReferences((prev) => prev.filter((r) => r._id !== id));
    try {
      await authFetch(`delete_reference/${id}?vertical_id=${verticalId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Failed to delete reference:', error);
    }
  };

  // Reuses the generic vertical order endpoint, exactly like the publications list.
  const reorderReferences = async (updated) => {
    setReferences(updated);
    try {
      await authFetch(`update_order?collection=references&vertical_id=${verticalId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated.map((r) => ({ id: r._id }))),
      });
    } catch (error) {
      console.error('Failed to reorder references:', error);
    }
  };

  return { references, loading, addReference, editReference, deleteReference, reorderReferences };
}
